import type { ReactElement } from 'react';
import { api, type Order } from '../shared/api/endpoints';
import { useMessages } from '../shared/i18n/locale';
import { useAsync } from '../shared/lib/use-async';
import { useLocationNames } from '../shared/lib/use-location-names';
import { Card, EmptyState, Field, OrderNumber, StatusChip } from '../shared/ui/primitives';

/**
 * «Товар у курьера» (разд. 9, FR-543…FR-546).
 *
 * Экран отдельный, а не фильтр в потоке, по одной причине: смена не завершается, пока
 * такие заказы есть (FR-545), и в конце дня это первое, что диспетчер обязан закрыть.
 *
 * Группировка **по водителю**: действие диспетчера адресовано человеку, который везёт
 * коробку, а не заказу.
 */

const REFRESH_MS = 30_000;

export function ReturnsPage(): ReactElement {
  const messages = useMessages();
  const locationName = useLocationNames();

  const orders = useAsync(() => api.orders({ goods_location: 'courier', limit: 200 }), [], {
    intervalMs: REFRESH_MS,
  });

  const groups = groupByDriver(orders.data?.data ?? []);

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-4)' }}>
      <h1>{messages.cabinet.returns.title}</h1>

      {groups.length === 0 ? (
        <EmptyState text={messages.cabinet.returns.empty} />
      ) : (
        groups.map((group) => (
          <Card key={group.driverId ?? 'unknown'} tone="wait">
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <h2>{group.driverName ?? messages.cabinet.flow.noDriver}</h2>
              <span className="num">{`${group.orders.length} ${messages.cabinet.returns.positions}`}</span>
            </div>

            {group.orders.map((order) => (
              <div
                key={order.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '160px 1fr 200px',
                  gap: 'var(--sp-3)',
                  alignItems: 'center',
                }}
              >
                <OrderNumber
                  externalNumber={order.external_number}
                  displayId={order.display_id ?? ''}
                />
                <Field
                  label={messages.cabinet.returns.returnTo}
                  value={locationName(order.pickup_location_id) ?? ''}
                />
                <StatusChip
                  status={order.status ?? 'new'}
                  goodsLocation={order.goods_location}
                  atRisk={order.at_risk === true}
                />
              </div>
            ))}
          </Card>
        ))
      )}
    </div>
  );
}

/** Группировка по водителю: действие адресовано человеку, а не заказу. */
export function groupByDriver(
  orders: readonly Order[],
): { driverId: string | null; driverName: string | null; orders: Order[] }[] {
  const groups = new Map<
    string,
    { driverId: string | null; driverName: string | null; orders: Order[] }
  >();

  for (const order of orders) {
    const key = order.driver?.id ?? 'unknown';
    const group = groups.get(key) ?? {
      driverId: order.driver?.id ?? null,
      driverName: order.driver?.name ?? null,
      orders: [],
    };

    group.orders.push(order);
    groups.set(key, group);
  }

  return [...groups.values()];
}
